import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Button } from "@mui/material";

import { CommentProps } from "../../../../types/components/props.type";
import { useCommentFormatter } from "../../../../hooks/useCommentFormatter";
import { CommentShowRepliesButtonIcon } from "../../../../assets/icons/create-feed";

import CommentBody from "./CommentBody";
import CommentHeader from "./CommentHeader";
import CommentActions from "./CommentActions";
import MakeCommentField from "../MakeCommentField";

const Comment = ({ comment, isLastComment, isParentCommentFieldVisible, mentionedUsers, postId }: CommentProps) => {
  const { t } = useTranslation("feed", { keyPrefix: "comment" });
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyToComment, setShowReplyToComment] = useState(false);

  const IS_LAST_COMMENT_WITH_HIDDEN_COMMENT_FIELD = isLastComment && !isParentCommentFieldVisible;

  const formattedComment = useCommentFormatter(comment);

  if (!formattedComment) return null;

  const {
    authorFirstTwoChr,
    authorFullName,
    authorMedia,
    authorRole,
    authorClub,
    timeAgo,
    commentText,
    isLiked,
    likesCount,
    replies,
    parentId,
  } = formattedComment;

  const handleShowReplies = () => {
    setShowReplies((prev) => !prev);
  };

  const handleReply = () => {
    setShowReplyToComment((prev) => !prev);
  };

  return (
    <Box sx={{ display: "flex", gap: "16px", flexDirection: "column", position: "relative" }}>
      <Box sx={{ display: "flex", gap: "8px" }}>
        <Avatar src={authorMedia} sx={{ width: 40, height: 40, position: "relative", zIndex: "10" }}>
          {authorFirstTwoChr}
        </Avatar>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              borderRadius: "0px 8px 8px 8px",
              background: "var(--Gray---50, #F5F6F5)",
              padding: "8px 12px",
              position: "relative",
            }}
          >
            <CommentHeader
              authorFullName={authorFullName}
              authorRole={authorRole}
              authorClub={authorClub}
              authorId={comment.authorId}
              commentId={comment.id}
              postId={postId}
            />
            <CommentBody commentText={commentText} mentionedUsers={mentionedUsers} />
          </Box>
          <CommentActions
            likesCount={likesCount}
            timeAgo={timeAgo}
            handleReply={handleReply}
            isLiked={isLiked}
            commentId={comment.id}
            postId={postId}
            isReply={Boolean(comment.parentId)}
          />
          {replies && replies?.length > 0 && (
            <Button
              onClick={handleShowReplies}
              sx={{
                color: "var(--Dark---1000, #111)",
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "20px",
                padding: 0,
                minWidth: "unset",
                width: "fit-content",
                background: "transparent !important",
              }}
              disableRipple
              endIcon={<CommentShowRepliesButtonIcon />}
            >
              {t(showReplies ? "hideReplies" : "showReplies", { count: replies?.length ?? 0 })}
            </Button>
          )}
        </Box>
      </Box>
      {parentId && (
        <Box
          sx={{
            borderBottom: "1px solid #DBDBDA",
            position: "absolute",
            top: "20px",
            left: "-28px",
            right: "100%",
          }}
        />
      )}
      {(showReplies || showReplyToComment) && (
        <Box
          sx={{
            borderLeft: "1px solid #DBDBDA",
            position: "absolute",
            top: "0",
            bottom: "0",
            left: "20px",
          }}
        />
      )}
      {showReplies && replies && replies.length > 0 && (
        <>
          <Box sx={{ ml: "48px", display: "flex", flexDirection: "column", gap: "16px" }}>
            {replies.map((reply, index) => (
              <Comment
                key={reply.id}
                comment={reply}
                isLastComment={replies.length - 1 === index}
                isParentCommentFieldVisible={showReplyToComment}
                mentionedUsers={mentionedUsers}
                postId={postId}
              />
            ))}
          </Box>
        </>
      )}
      {IS_LAST_COMMENT_WITH_HIDDEN_COMMENT_FIELD && (
        <Box
          sx={{
            borderLeft: "1px solid #FFF",
            position: "absolute",
            top: "21px",
            bottom: "0",
            left: "-28px",
            zIndex: showReplies ? "unset" : 20,
          }}
        />
      )}
      {showReplyToComment && (
        <Box sx={{ ml: "48px", position: "relative" }}>
          <MakeCommentField parentComment={comment} postId={postId} />
          <Box
            sx={{
              borderLeft: "2px solid #FFF",
              position: "absolute",
              top: "21px",
              bottom: "0",
              left: "-28px",
            }}
          />
          <Box
            sx={{
              borderBottom: "1px solid #DBDBDA",
              position: "absolute",
              top: "20px",
              left: "-28px",
              right: "100%",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default Comment;
